import React, { useContext, useEffect, useState, useRef } from 'react'; // Added useRef
import './PackagesBottomSheet.css';
import { getAppEnv } from '../../../config/env';
import { Context } from '../../AppContext';
// import { logRttEventLogger } from '../../logRttRequestBuilder';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

interface PackageInfo {
  title: string;
  sub_title: string;
  package_name: string;
  package_description: Array<{
    icon: string;
    text: string;
  }>;
  city_list: Array<{
    image_url: string;
    name: string;
  }>;
  view_cta_title: string;
  view_cta_sub_title: string;
  close_cta: string;
  redirection_url: string;
  mweb_redirection_url: string;
}

interface PackagesBottomSheetProps {
  packageData: PackageInfo;
  pageUrl: string;
  onClose: () => void;
}

const PackagesBottomSheet: React.FC<PackagesBottomSheetProps> = ({
  packageData,
  pageUrl,
  onClose
}) => {
  const { trackTemplateEvent, omniUrlArgs } = useContext(Context);
  const descriptionLength = packageData.package_description.length;
  const [currentDescIndex, setCurrentDescIndex] = useState(0);

  const viewCtaButtonRef = useRef<HTMLButtonElement>(null); // Ref for the view-cta button
  const sheetContentRef = useRef<HTMLDivElement>(null); // Ref for the sheet-content

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDescIndex(prev => (prev + 1) % descriptionLength);
    }, 1500);
    return () => clearInterval(timer);
  }, [descriptionLength]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // If page is restored from bfcache, remove loading states
        if (viewCtaButtonRef.current) {
          viewCtaButtonRef.current.classList.remove('button-loading-active');
          viewCtaButtonRef.current.disabled = false;
        }
        if (sheetContentRef.current) {
          sheetContentRef.current.classList.remove('loading');
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      // Fallback cleanup if component unmounts while loading (less likely for bfcache)
      if (
        viewCtaButtonRef.current &&
        viewCtaButtonRef.current.classList.contains('button-loading-active')
      ) {
        viewCtaButtonRef.current.classList.remove('button-loading-active');
        viewCtaButtonRef.current.disabled = false;
      }
      if (
        sheetContentRef.current &&
        sheetContentRef.current.classList.contains('loading')
      ) {
        sheetContentRef.current.classList.remove('loading');
      }
    };
  }, []); // Empty dependency array, runs once on mount

  const isAtLoadingState = () => {
    return (
      viewCtaButtonRef.current?.classList.contains('button-loading-active') ||
      false
    );
  };

  const handleViewCta = (event: React.MouseEvent<HTMLButtonElement>) => {
    const button = viewCtaButtonRef.current; // Use ref
    const sheetContent = sheetContentRef.current; // Use ref

    if (!button || button.classList.contains('button-loading-active')) {
      return; // Already loading or button not found
    }

    if (sheetContent) {
      sheetContent.classList.add('loading');
    }

    button.classList.add('button-loading-active');
    button.disabled = true;

    // Redirection logic
    if (packageData.mweb_redirection_url) {
      // logRttEventLogger({omniUrlArgs, eventName: 'Cab_SEO_Packages_Mobile_BottomSheet_Clicked',pageUrl });
      window.location.href = `${packageData.mweb_redirection_url}&${omniUrlArgs}`;
      trackTemplateEvent(`Cab_SEO_Packages_Mobile_BottomSheet_Clicked`);
      // Loader class remains, pageshow event will handle cleanup if user navigates back
    } else {
      // Fallback if no redirection URL
      console.warn(
        'No redirection URL found for bottom sheet. Removing loader.'
      );
      setTimeout(() => {
        // Remove loader after a short delay if not redirecting
        if (sheetContent) {
          sheetContent.classList.remove('loading');
        }
        if (button) {
          button.classList.remove('button-loading-active');
          button.disabled = false;
        }
      }, 7000);
    }
  };

  return (
    <div className="packages-overlay" onClick={onClose}>
      <div className="packages-bottom-sheet" onClick={e => e.stopPropagation()}>
        <div className="sheet-content" ref={sheetContentRef}>
          {' '}
          {/* Assign ref */}
          <div className="header">
            <h2>{packageData.title}</h2>
          </div>
          <div className="package-details">
            <p className="sub-title">{packageData.sub_title}</p>
            <h3 className="package-name">{packageData.package_name}</h3>
            <div className="package-description-container">
              <div className="description-wrapper" key={currentDescIndex}>
                <img
                  src={`${PUBLIC_URL}/static/public/sealTick.png`}
                  alt=""
                  className="description-icon"
                />
                <p className="package-description">
                  {packageData.package_description[currentDescIndex].text}
                </p>
              </div>
            </div>
          </div>
          <div className="city-list">
            {packageData.city_list.map((city, index) => (
              <>
                <div key={index} className="city-item">
                  <img
                    src={`${PUBLIC_URL}/static/public/${city.image_url}`}
                    alt={city.name}
                  />
                  <span>{city.name}</span>
                </div>
              </>
            ))}
          </div>
          <div className="cta-container">
            <button
              className="view-cta"
              onClick={handleViewCta}
              ref={viewCtaButtonRef} /* Assign ref */
            >
              <div className="cta-text-container">
                <span className="title-text">{packageData.view_cta_title}</span>
                <span className="subtitle-text">
                  {packageData.view_cta_sub_title}
                </span>
              </div>
              <div className="button-loader-element"></div>
            </button>
            <button
              className="close-cta"
              onClick={() => {
                !isAtLoadingState() && onClose();
              }}
            >
              {packageData.close_cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagesBottomSheet;
